import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, ColumnLayout, Link, Modal, SpaceBetween, Spinner, Table, Tabs,
} from "@cloudscape-design/components";
import { apiFetch } from "../../../auth/apiClient";

const frameStyle = { width: "100%", height: "55vh", border: "1px solid #d5dbdb", background: "#fff" };

const buildContentDocument = bodyHtml => `<!doctype html><html><head>
  <meta charset="utf-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'">
  <style>body { font: 14px/1.5 Arial, sans-serif; color: #16191f; margin: 20px; } table { max-width: 100%; } th, td { text-align: left; }</style>
  </head><body>${bodyHtml || ""}</body></html>`;

const resolvePreviewKind = (document, payload) => {
  if (payload.preview?.kind === "html") return "html";
  const name = payload.filename || document.name || "";
  const mime = (document.mimeType || "").toLowerCase();
  if (payload.preview?.kind === "pdf" || mime === "application/pdf" || /\.pdf$/i.test(name)) return "pdf";
  if (/^image\/(png|jpe?g|gif|webp|bmp|svg\+xml|avif)$/.test(mime) || /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(name)) return "image";
  return "unsupported";
};

function BundleDocumentPreview({ document, packetId }) {
  const [state, setState] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const owner = `${packetId}:${document.id}`;
  const current = state?.owner === owner ? state : null;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState(null);
    const load = async () => {
      try {
        const response = await apiFetch(
          `/api/documents/${encodeURIComponent(document.id)}/presign-download?paymentPacketId=${encodeURIComponent(packetId)}`,
          { signal: controller.signal },
        );
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.message || "Unable to open this document.");
        if (!payload.presigned?.url) throw new Error("Document preview is unavailable.");
        const url = new URL(payload.presigned.url, window.location.origin);
        if (!["https:", "http:"].includes(url.protocol)) throw new Error("Document preview is unavailable.");
        if (active) setState({ owner, url: url.href, kind: resolvePreviewKind(document, payload) });
      } catch (error) {
        if (active && !controller.signal.aborted) setState({ owner, error: error.message || "Unable to open this document." });
      }
    };
    load();
    return () => { active = false; controller.abort(); };
  }, [owner, packetId, document, attempt]);

  return (
    <SpaceBetween size="s">
      <Box variant="h3">{document.name}</Box>
      {!current && <Box><Spinner /> Loading document…</Box>}
      {current?.error && (
        <Alert type="error" action={<Button onClick={() => setAttempt(value => value + 1)}>Retry</Button>}>
          {current.error}
        </Alert>
      )}
      {current?.url && (
        <>
          {current.kind === "image" ? (
            <img src={current.url} alt={document.name} style={{ maxWidth: "100%", maxHeight: "65vh", objectFit: "contain" }} />
          ) : current.kind === "unsupported" ? (
            <Box>This file type cannot be previewed here.</Box>
          ) : (
            <iframe
              title={`Document preview: ${document.name}`}
              src={current.url}
              style={frameStyle}
              sandbox={current.kind === "html" ? "" : undefined}
              referrerPolicy="no-referrer"
            />
          )}
          <Link href={current.url} external target="_blank">Open document in a new tab</Link>
        </>
      )}
    </SpaceBetween>
  );
}

export default function PaymentCommunicationContentModal({ communication, onDismiss }) {
  const packetId = String(communication.packetId);
  const owner = `${packetId}:${communication.id}`;
  const [state, setState] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [activeTabId, setActiveTabId] = useState("communication");
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const current = state?.owner === owner ? state : null;
  const preview = current?.preview;
  const documents = preview?.documents || [];
  const selectedDocument = documents.find(document => document.id === selectedDocumentId) || null;
  const usesFinanceTemplate = communication.direction === "outbound" && communication.template === "payment_packet";

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState(null);
    setSelectedDocumentId(null);
    const load = async () => {
      try {
        const response = await apiFetch(`/api/finance/payment-packets/${encodeURIComponent(packetId)}/content-preview`, {
          signal: controller.signal,
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.message || "Unable to load the payment content.");
        if (String(payload.packetId) !== packetId) throw new Error("The payment content does not match the selected packet.");
        if (active) setState({ owner, preview: payload });
      } catch (error) {
        if (active && !controller.signal.aborted) setState({ owner, error: error.message || "Unable to load the payment content." });
      }
    };
    load();
    return () => { active = false; controller.abort(); };
  }, [owner, packetId, attempt]);

  const documentColumns = [
    {
      id: "name",
      header: "Document",
      cell: document => <Link onFollow={() => setSelectedDocumentId(document.id)}>{document.name}</Link>,
    },
  ];

  return (
    <Modal
      visible
      size="max"
      header="Payment communication content"
      onDismiss={onDismiss}
      footer={<Box float="right"><Button onClick={onDismiss}>Close</Button></Box>}
    >
      <SpaceBetween size="m">
        {!current && <Box><Spinner /> Loading payment content…</Box>}
        {current?.error && (
          <Alert type="error" action={<Button onClick={() => setAttempt(value => value + 1)}>Retry</Button>}>
            {current.error}
          </Alert>
        )}
        {preview && (
          <Tabs
            activeTabId={activeTabId}
            onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
            tabs={[
              {
                id: "communication",
                label: "Communication",
                content: (
                  <SpaceBetween size="m">
                    <Box variant="h3">{usesFinanceTemplate ? preview.subject : communication.subject || "Communication"}</Box>
                    {usesFinanceTemplate ? (
                      <iframe title="Communication content" srcDoc={buildContentDocument(preview.bodyHtml)} sandbox="" style={frameStyle} />
                    ) : (
                      <Box><div style={{ whiteSpace: "pre-wrap" }}>{communication.body || "No message content was recorded."}</div></Box>
                    )}
                  </SpaceBetween>
                ),
              },
              {
                id: "bundle",
                label: `Bundle documents (${documents.length})`,
                content: (
                  <ColumnLayout columns={selectedDocument ? 2 : 1}>
                    <Table
                      ariaLabels={{ tableLabel: "Bundle documents" }}
                      trackBy="id"
                      items={documents}
                      columnDefinitions={documentColumns}
                      selectionType="single"
                      selectedItems={selectedDocument ? [selectedDocument] : []}
                      onSelectionChange={({ detail }) => setSelectedDocumentId(detail.selectedItems[0]?.id || null)}
                      onRowClick={({ detail }) => setSelectedDocumentId(detail.item.id)}
                      empty={<Box padding="m">No documents are currently linked to this packet's bundle.</Box>}
                      variant="embedded"
                    />
                    {selectedDocument && <BundleDocumentPreview key={`${owner}:${selectedDocument.id}`} document={selectedDocument} packetId={packetId} />}
                  </ColumnLayout>
                ),
              },
            ]}
          />
        )}
      </SpaceBetween>
    </Modal>
  );
}
